import 'react-fancybox/lib/fancybox.css';

import { Chip } from '@mui/material';
import axios from 'axios';
import React, { useEffect } from 'react';
import BootstrapTable from 'react-bootstrap-table-next';
import cellEditFactory from 'react-bootstrap-table2-editor';
import paginationFactory, {
  PaginationProvider,
  PaginationTotalStandalone,
  SizePerPageDropdownStandalone,
} from 'react-bootstrap-table2-paginator';
import ToolkitProvider from 'react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit';
import { isMobile } from 'react-device-detect';
import { useSearchParams } from 'react-router-dom';

import Breadcrumb from '@/components/common/breadcrumb.component';
import DebounceSearchBar from '@/components/debounce-search-bar';
import Icon from '@/components/icon';
import NoDataIndicator from '@/components/no-data-indicator';
import StandalonePagination from '@/components/pagination/standalone';
import RowMoreOptions from '@/components/table/row-more-options';
import TableLoader from '@/components/table-loader';
import { Constants } from '@/constants';
import { UserStore } from '@/stores';

interface ManagedUser {
  id: number;
  user_id?: number | string | null;
  firebase_id?: string | null;

  first_name?: string | null;
  last_name?: string | null;
  full_name?: string | null;
  email?: string | null;
  image_url?: string | null;

  active?: boolean | null;
  email_verified?: boolean | null;

  created_at?: string | null;
  updated_at?: string | null;
}

const getUserName = (user: ManagedUser) => {
  if (user.full_name) {
    return user.full_name;
  }

  const name = [user.first_name, user.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();

  return name || user.email || `User #${user.id}`;
};

const getUserInitials = (user: ManagedUser) => {
  const parts = [user.first_name, user.last_name].filter(Boolean);

  if (parts.length) {
    return parts
      .slice(0, 2)
      .map((part) => part?.charAt(0).toUpperCase())
      .join('');
  }

  return user.email?.charAt(0).toUpperCase() || '?';
};

const formatDate = (value?: string | null) => {
  if (!value) {
    return '—';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const defaultSorted = [
  {
    dataField: 'brand',
    order: 'asc',
  },
];

const cellEditProps = {
  mode: 'dbclick',
  beforeSaveCell(
    oldValue: any,
    newValue: any,
    row: any,
    column: any,
    done: any
  ) {
    console.log(`Before save for ${column.dataField}`);
    // TODO: call done() on success or done(false) on failure
  },
  afterSaveCell: (oldValue: any, newValue: any, row: any, column: any) => {
    console.log(`After save for ${column}`);
  },
};

const UserAvatar: React.FC<{
  user: ManagedUser;
}> = ({ user }) => {
  const [imageFailed, setImageFailed] = React.useState(false);
  const showImage = Boolean(user.image_url) && !imageFailed;

  return (
    <div
      style={{
        width: 42,
        height: 42,
        flexShrink: 0,
        overflow: 'hidden',
        borderRadius: '50%',
        border: '1px solid rgba(211, 169, 102, 0.45)',
        background: 'rgba(211, 169, 102, 0.14)',
        color: '#80612f',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 13,
        fontWeight: 700,
      }}
    >
      {showImage ? (
        <img
          src={user.image_url!}
          alt=""
          onError={() => setImageFailed(true)}
          style={{
            width: '100%',
            height: '100%',
            display: 'block',
            objectFit: 'cover',
          }}
        />
      ) : (
        getUserInitials(user)
      )}
    </div>
  );
};

const UserTable: React.FC<any> = () => {
  const {
    users,
    totalUserCount,
    loadingUsers: loading,
  } = UserStore.useState((state) => state);

  const [tableState, setTableState] = React.useState({
    page: 1,
    sizePerPage: 30,
    searchText: '',
  });
  const [searchParams] = useSearchParams();

  const getUsers = (page: number, sizePerPage: number, searchText?: string) => {
    UserStore.update((state) => {
      state.loadingUsers = true;
    });

    axios
      .get(`${Constants.apiPath}/users/datatables/all`, {
        params: {
          page,
          limit: sizePerPage,
          q: searchText || undefined,
        },
      })
      .then((res) => {
        UserStore.update((state) => {
          state.users = res.data.data;
          state.totalUserCount = res.data.recordsTotal;
        });
      })
      .catch((error) => {
        console.error(error);

        UserStore.update((state) => {
          state.users = [];
          state.totalUserCount = 0;
        });
      })
      .finally(() => {
        UserStore.update((state) => {
          state.loadingUsers = false;
        });
      });
  };

  useEffect(() => {
    getUsers(
      tableState.page,
      tableState.sizePerPage,
      tableState.searchText || undefined
    );
  }, []);

  const editBtnFormatter = (cell: any, row: any, index: number) => (
    <div style={{ minWidth: 68 }}>
      <div id={`dropdown-actions-${index}`} className="dropdown-actions d-none">
        <Icon
          name="edit2"
          className="ml-1 mr-3"
          style={{
            cursor: 'pointer',
            display: 'inline',
            height: 18,
            width: 18,
          }}
          onClick={() => {
            // const cigar = row;
            // navigate(
            //   `/cigars/editor/${cigar.hash_id || hashids.encode(cigar.id)}`,
            //   { state: { cigar } }
            // );
          }}
        />
        {actionDropdown(index, row)}
      </div>
    </div>
  );

  const updateRecords = () => {
    // const { page, sizePerPage, searchText, tableChangeType } = state;
    // handleTableChange(tableChangeType, {
    //   page: page - 1 > 0 ? page - 1 : 1,
    //   sizePerPage,
    //   searchText,
    // });
    // setTimeout(() => {
    //   handleTableChange(tableChangeType, { page, sizePerPage, searchText });
    // }, 50);
  };

  const handleRowChange = (updatedCigar: any) => {
    // const updatedCigars = state.cigars.map((cigar: any) =>
    //   cigar.id === updatedCigar.id ? { ...cigar, ...updatedCigar } : cigar
    // );
    updateRecords();
  };

  const handleRowDelete = (deletedCigar: any) => {
    // const updatedCigars = state.cigars.filter(
    //   (cigar: any) => cigar.id !== deletedCigar.id
    // );
  };

  const handleTableChange = (
    _type: string,
    {
      page,
      sizePerPage,
      searchText,
    }: {
      page?: number;
      sizePerPage?: number;
      searchText?: string;
    }
  ) => {
    setTableState((current) => {
      const nextSearchText =
        searchText !== undefined ? searchText : current.searchText;

      const nextSizePerPage = sizePerPage ?? current.sizePerPage;

      const searchChanged = nextSearchText !== current.searchText;

      const sizeChanged = nextSizePerPage !== current.sizePerPage;

      const nextPage =
        searchChanged || sizeChanged ? 1 : (page ?? current.page);

      getUsers(nextPage, nextSizePerPage, nextSearchText || undefined);

      return {
        page: nextPage,
        sizePerPage: nextSizePerPage,
        searchText: nextSearchText,
      };
    });
  };

  const actionDropdown = (index: number, user: ManagedUser) => (
    <RowMoreOptions
      row={index}
      items={[
        {
          type: 'MenuItem',
          onClick: () => {
            alert('Coming soon!');
            // navigate(`/users/${user.id}`, {
            //   state: { user },
            // });
          },
          title: 'View User',
        },
        {
          type: 'MenuItem',
          onClick: () => {},
          title: 'Send Password Reset Email',
          disabled: !user.email,
        },
        {
          type: 'MenuItem',
          onClick: () => {},
          title: 'Resend Email Verification',
          disabled: !user.email || user.email_verified,
        },
        {
          type: 'Divider',
        },
        {
          type: 'MenuItem',
          onClick: () => {},
          style: {
            color: user.active === false ? undefined : '#ff4700',
          },
          title:
            user.active === false ? 'Reactivate Account' : 'Deactivate Account',
        },
      ]}
      onShow={() => {
        document
          .querySelector(`#dropdown-actions-${user.id}`)
          ?.classList.add('d-none');
      }}
    />
  );

  const columns = [
    {
      dataField: 'full_name',
      text: 'User',
      sort: true,
      headerClasses: 'sticky-header',
      formatter: (_cell: unknown, user: ManagedUser) => (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            minWidth: 250,
          }}
        >
          <UserAvatar user={user} />

          <div style={{ minWidth: 0 }}>
            <div
              style={{
                color: '#252525',
                fontWeight: 600,
                lineHeight: 1.35,
              }}
            >
              {getUserName(user)}
            </div>

            <div
              className="text-muted"
              style={{
                maxWidth: 240,
                overflow: 'hidden',
                fontSize: 12,
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={user.email || undefined}
            >
              {user.email || 'No email address'}
            </div>
          </div>
        </div>
      ),
    },
    {
      dataField: 'user_id',
      text: 'Account',
      sort: true,
      headerClasses: 'sticky-header',
      formatter: (value: number | string | null, user: ManagedUser) => (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            marginTop: 4,
          }}
        >
          <Chip size="small" label={user.account_type} variant="outlined" />
          <span className="text-muted" style={{ whiteSpace: 'nowrap' }}>
            {value ?? '—'}
          </span>
        </div>
      ),
    },
    {
      dataField: 'firebase_id',
      text: 'Authentication',
      sort: false,
      headerClasses: 'sticky-header',
      formatter: (value: string | null) => (
        <div style={{ minWidth: 140 }}>
          {value ? (
            <>
              {/* FIXME More useful here is the method they used to sign up - Google, FB, etc */}
              <Chip size="small" label="Firebase" variant="outlined" />

              <div
                className="text-muted"
                title={value}
                style={{
                  maxWidth: 160,
                  marginTop: 5,
                  overflow: 'hidden',
                  fontSize: 11,
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {value}
              </div>
            </>
          ) : (
            <Chip
              size="small"
              label="No auth account"
              color="warning"
              variant="outlined"
            />
          )}
        </div>
      ),
    },
    {
      dataField: 'active',
      text: 'Status',
      sort: false,
      headerClasses: 'sticky-header',
      formatter: (_cell: unknown, user: ManagedUser) => (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 5,
            minWidth: 125,
          }}
        >
          <Chip
            size="small"
            color={user.active === false ? 'default' : 'success'}
            variant="outlined"
            label={user.active === false ? 'Inactive' : 'Active'}
          />

          {/* <Chip */}
          {/*  size="small" */}
          {/*  color={user.email_verified ? 'success' : 'warning'} */}
          {/*  variant="outlined" */}
          {/*  label={user.email_verified ? 'Email verified' : 'Unverified email'} */}
          {/* /> */}
        </div>
      ),
    },
    {
      dataField: 'sign_up_timestamp',
      text: 'Joined',
      sort: true,
      headerClasses: 'sticky-header',
      formatter: (value: string | null) => (
        <span style={{ whiteSpace: 'nowrap' }}>{formatDate(value)}</span>
      ),
    },
    {
      dataField: 'actions',
      text: '',
      formatter: editBtnFormatter,
      sort: false,
      editable: false,
      headerClasses: 'sticky-header',
    },
  ];

  const rowEvents = {
    onMouseEnter: (e: any, row: any, index: number) => {
      document
        .querySelector(`#dropdown-actions-${index}`)
        ?.classList.remove('d-none');
    },
    onMouseLeave: (e: any, row: any, index: number) => {
      document
        .querySelector(`#dropdown-actions-${index}`)
        ?.classList.add('d-none');
    },
    // onDoubleClick: (e: any, row: any, index: number) => {
    //   // Double click event if needed
    // },
  };

  return (
    <div>
      <Breadcrumb title="User Manager" label="Manager" parent="Users" />

      <div className="container-fluid">
        {searchParams?.get('id') && (
          <Chip
            size="small"
            label={`id: ${searchParams?.get('id')}`}
            // onClick={clearQueryParams}
            // onDelete={clearQueryParams}
          />
        )}
        <div className="row">
          <div className="col-sm-12">
            <div
              className="m-b-10"
              style={
                isMobile ? { background: 'transparent', boxShadow: 'none' } : {}
              }
            >
              <div className="datatable-react">
                <ToolkitProvider
                  keyField="id"
                  data={users}
                  columns={columns}
                  search
                >
                  {(toolkitprops: any) => (
                    <div>
                      <div style={{ display: 'flex', marginTop: 20 }}>
                        {isMobile && (
                          <DebounceSearchBar
                            {...toolkitprops.searchProps}
                            placeholder="Search users..."
                            // onChange={() => updateState({ page: 1 })}
                            // onSearch={(query) => {
                            //   // INFO Since mobile doesn't use BootstrapTable, this needs managed manually
                            //   updateState({ searchText: query });
                            // }}
                          />
                        )}
                        {!isMobile && (
                          <div style={{ flex: 1 }}>
                            <DebounceSearchBar
                              {...toolkitprops.searchProps}
                              // value={state.searchText}
                              placeholder="Search users..."
                              // onChange={() => updateState({ page: 1 })}
                            />
                          </div>
                        )}
                      </div>
                      <PaginationProvider
                        pagination={paginationFactory({
                          page: tableState.page,
                          sizePerPage: tableState.sizePerPage,
                          custom: true,
                          totalSize: totalUserCount,
                          showTotal: true,
                        })}
                      >
                        {({ paginationProps, paginationTableProps }: any) => (
                          <div>
                            <BootstrapTable
                              remote
                              keyField="id"
                              data={users}
                              columns={columns}
                              rowEvents={rowEvents}
                              defaultSorted={defaultSorted}
                              wrapperClasses="table-responsive"
                              {...paginationTableProps}
                              cellEdit={cellEditFactory(cellEditProps)}
                              onTableChange={handleTableChange}
                              noDataIndication={() =>
                                !loading ? (
                                  <NoDataIndicator message="No users found matching search query." />
                                ) : (
                                  <TableLoader />
                                )
                              }
                              {...toolkitprops.baseProps}
                            />

                            <PaginationTotalStandalone {...paginationProps} />

                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                paddingBottom: 20,
                              }}
                            >
                              <div style={{ flex: 1 }}>
                                <StandalonePagination
                                  paginationProps={paginationProps}
                                />
                              </div>

                              <SizePerPageDropdownStandalone
                                {...paginationProps}
                              />
                            </div>
                          </div>
                        )}
                      </PaginationProvider>
                    </div>
                  )}
                </ToolkitProvider>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserTable;
